import { useNavigate } from "react-router-dom";
import { FaFileAlt, FaUserTie, FaBuilding, FaGlobe, FaQuestion, FaCheck,} from "react-icons/fa";
import "./GettingStartedShared.css";

const General4 = () => {
    const navigate = useNavigate();

    const relatedArticles = [
        {
            title: "Opening a US Bank Account as a Non-Resident",
            description: "Learn the requirements and process for non-residents to open a US business bank account.",
            link: "/opening-a-us-business-bank-account-as-a-non-resident-what-are-your-options?"
        },
    ];

    const einFaqs = [
        {
            question: "What is an EIN used for?",
            answer: "An EIN is required for opening a US business bank account, filing US taxes, hiring employees, and many other business activities."
        },
        {
            question: "Can I get an EIN without an SSN?",
            answer: "Yes, non-US residents can obtain an EIN without an SSN by using an Individual Taxpayer Identification Number (ITIN) or by applying directly with their passport."
        },
        {
            question: "How long does it take to get an EIN?",
            answer: "When applying online, you typically receive your EIN immediately. Mail and fax applications can take 4-6 weeks to process."
        }
    ];

    return (
        <div className="gs-container">
            <header className="gs-header">
                <div className="gs-container-1200">
                </div>
            </header>
            
            <main className="gs-main-content">
                <div className="gs-container-1200">
                    <nav className="gs-breadcrumb">
                        <ol>
                            <li><a href="/help">Help Center</a></li>
                            <li><a href="/getting-started">Getting Started</a></li>
                            <li>General</li>
                        </ol>
                    </nav>

                    <article className="gs-article">
                        <header className="gs-article-header">
                            <h1 className="gs-article-title">EIN (Employer Identification Number): What It Is?</h1>
                            <div className="gs-article-meta">
                                <span>Posted by BooksComply Team</span>
                                <span>•</span>
                                <span>Last updated August 2025</span>
                            </div>
                        </header>
                        
                        <div className="gs-article-content">
                            <div className="gs-intro-card">
                                <FaFileAlt className="gs-intro-icon" />
                                <p>
                                    If you're a non-US resident or non-US citizen looking to run a business in the United States, one
                                    of the first steps after incorporation is obtaining an Employer Identification Number (EIN). Even if
                                    you don't plan to hire employees, this number is critical for opening a US bank account, filing
                                    taxes, and operating legally.
                                </p>
                                <p>
                                    Here's everything you need to know about getting an EIN—even if you don't have a Social
                                    Security Number (SSN).
                                </p>
                            </div>

                            <h2><FaFileAlt className="section-icon" /> What is an EIN?</h2>
                            <p>
                                The Employer Identification Number (EIN) is a unique 9-digit number issued by the IRS
                            (Internal Revenue Service) to identify your business for tax purposes. Think of it as a Social
                            Security Number (SSN) for your business.
                        </p>
                        <div className="gs-highlight-box">
                            <p><strong>Format:</strong> XX-XXXXXXX (e.g., 12-3456789)</p>
                        </div>

                        <h2><FaUserTie className="section-icon" /> Why Do You Need an EIN?</h2>
                        <p>
                            Even if you're a solopreneur or small business owner, an EIN is required for:
                        </p>
                        <div className="gs-feature-grid">
                            <div className="gs-feature-card">
                                <div className="gs-feature-icon">
                                    <FaBuilding />
                                </div>
                                <h3>Banking</h3>
                                <p>Opening a US business bank account is nearly impossible without an EIN.</p>
                            </div>
                            <div className="gs-feature-card">
                                <div className="gs-feature-icon">
                                    <FaFileAlt />
                                </div>
                                <h3>Taxes</h3>
                                <p>Required for filing business tax returns with the IRS.</p>
                            </div>
                            <div className="gs-feature-card">
                                <div className="gs-feature-icon">
                                    <FaUserTie />
                                </div>
                                <h3>Hiring</h3>
                                <p>Necessary for hiring employees or contractors in the US.</p>
                            </div>
                            <div className="gs-feature-card">
                                <div className="gs-feature-icon">
                                    <FaGlobe />
                                </div>
                                <h3>Compliance</h3>
                                <p>Required for business licenses, permits, and legal operations.</p>
                            </div>
                        </div>

                        <h2><FaQuestion className="section-icon" /> Can Non-US Residents Get an EIN?</h2>
                        <div className="gs-alert success">
                            <p><strong>Yes!</strong> You don't need to be a US citizen or have a Social Security Number (SSN) to get an EIN.</p>
                        </div>
                        <p>
                            Non-residents typically apply using one of these methods:
                        </p>
                        <div className="gs-steps">
                            <div className="gs-step">
                                <div className="gs-step-number">1</div>
                                <div className="gs-step-content">
                                    <h4>Online (Fastest)</h4>
                                    <p>Use our service to apply on your behalf (recommended for non-residents).</p>
                                </div>
                            </div>
                            <div className="gs-step">
                                <div className="gs-step-number">2</div>
                                <div className="gs-step-content">
                                    <h4>By Mail or Fax (SS-4 Form)</h4>
                                    <p>Slower but available for those without an SSN.</p>
                                </div>
                            </div>
                            <div className="gs-step">
                                <div className="gs-step-number">3</div>
                                <div className="gs-step-content">
                                    <h4>Phone (Limited Availability)</h4>
                                    <p>Call the IRS Business & Specialty Tax Line.</p>
                                </div>
                            </div>
                        </div>

                        <h2><FaFileAlt className="section-icon" /> What You'll Need to Apply</h2>
                        <p>
                            To get an EIN as a non-resident, you'll typically need:
                        </p>
                        <ul className="gs-checklist">
                            <li><FaCheck className="check-icon" /> Your business name and legal structure (LLC, Corporation, etc.)</li>
                            <li><FaCheck className="check-icon" /> Business address (can be outside the US)</li>
                            <li><FaCheck className="check-icon" /> Name and details of the responsible party (owner/officer)</li>
                            <li><FaCheck className="check-icon" /> Reason for applying (starting a new business, banking, taxes, etc.)</li>
                        </ul>

                        <h2><FaQuestion className="section-icon" /> Frequently Asked Questions</h2>
                        <div className="gs-faq">
                            {einFaqs.map((faq, index) => (
                                <div key={index} className="gs-faq-item">
                                    <h3>{faq.question}</h3>
                                    <p>{faq.answer}</p>
                                </div>
                            ))}
                        </div>

                        {/* <div className="gs-cta-box">
                            <h3>Need Help Getting an EIN?</h3>
                            <p>Our team specializes in helping non-US residents obtain EINs quickly and easily, even without an SSN.</p>
                            <div className="gs-cta-buttons">
                                <button 
                                    className="gs-cta-btn"
                                    onClick={() => navigate('/services/ein-application')}
                                >
                                    <FaFileAlt /> Learn About Our EIN Service
                                </button>
                                <button 
                                    className="gs-cta-btn secondary"
                                    onClick={() => navigate('/contact')}
                                >
                                    <FaEnvelope /> Contact Support
                                </button>
                            </div>
                        </div> */}
                    </div>
                    </article>
                    
                    {/* <div className="gs-helpful-section">
                        <h3 className="gs-helpful-title">Was this article helpful?</h3>
                        <div className="gs-helpful-buttons">
                            <button className="gs-helpful-btn positive">
                                <FaCheckCircle /> Yes, it was helpful
                            </button>
                            <button className="gs-helpful-btn negative">
                                <FaTimesCircle /> No, I need more help
                            </button>
                        </div>
                    </div> */}
                    
                    <div className="gs-related-articles">
                        <h2 className="gs-related-title">Related Articles</h2>
                        <div className="gs-related-grid">
                            {relatedArticles.map((article, index) => (
                                <a 
                                    key={index} 
                                    href={article.link} 
                                    className="gs-related-card"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate(article.link);
                                    }}
                                >
                                    <h3>{article.title}</h3>
                                    <p>{article.description}</p>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default General4;